/**
 * "Manage Workshop" — the power-user editor for per-workshop content
 * overrides. One surface, a left-rail switcher over the editable sections;
 * each section reads the resolved value, writes a field patch via the
 * effective-override helpers (program defaults vs client custom), and
 * offers per-section "Reset to default". A header "Reset all" clears
 * every override for the active target.
 *
 * Content mode (default vs custom for this client) sits in the header:
 *   - Use default → edits land in `_shared/workshop-defaults.json`
 *   - Custom for this client → edits land in the client's override blob
 *
 * Gated by power-user mode AND `can(user, "manage:program")`.
 */

import { useEffect, useMemo, useState } from "react";
import { RotateCcw, Settings2, X } from "lucide-react";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { PanelDialogContent } from "@/components/shell/PanelDialogContent";
import { WORKSHOPS, type Workshop } from "@/content/workshops";
import { useAuth } from "@/hooks/use-auth";
import { usePowerUser } from "@/stores/usePowerUser";
import { can } from "@/lib/auth/can";
import { useActiveClient } from "@/lib/active-client";
import { workshopOverrideEditor as content } from "@/content/ui";
import {
  clearEffectiveOverrides,
  setContentMode,
  useEffectiveWorkshopOverride,
  useWorkshopContentMode,
} from "@/stores/useEffectiveWorkshopOverride";
import { useWorkshopContentDefaults } from "@/stores/useWorkshopContentDefaults";
import { useWorkshopOverrides } from "@/stores/useWorkshopOverrides";
import type { ContentMode, WorkshopOverride } from "./workshop-override-content";
import { CrewEditor } from "./sections/CrewEditor";
import { BriefEditor } from "./sections/BriefEditor";
import { IntroVideoEditor } from "./sections/IntroVideoEditor";
import { QuestionnaireEditor } from "./sections/QuestionnaireEditor";
import { AgendaEditor } from "./sections/AgendaEditor";
import { BringEditor } from "./sections/BringEditor";
import { LogisticsEditor } from "./sections/LogisticsEditor";
import { PreReadingEditor } from "./sections/PreReadingEditor";
import { RunSequenceEditor } from "./sections/RunSequenceEditor";
import { DiagramEditor } from "./sections/DiagramEditor";

interface Props {
  open: boolean;
  onClose: () => void;
  workshopId: Workshop["id"];
}

const SECTIONS = [
  { key: "diagram", label: "Bubble diagram", Comp: DiagramEditor },
  { key: "crew", label: "Meet the Crew", Comp: CrewEditor },
  { key: "brief", label: "Brief rows", Comp: BriefEditor },
  { key: "intro", label: "Intro video", Comp: IntroVideoEditor },
  { key: "questionnaire", label: "The questions", Comp: QuestionnaireEditor },
  { key: "run-sequence", label: "Run sequence", Comp: RunSequenceEditor },
  { key: "agenda", label: "Agenda", Comp: AgendaEditor },
  { key: "bring", label: "What to bring", Comp: BringEditor },
  { key: "logistics", label: "Logistics", Comp: LogisticsEditor },
  { key: "doc", label: "Pre-reading", Comp: PreReadingEditor },
] as const;

type SectionKey = (typeof SECTIONS)[number]["key"];

const SECTION_OVERRIDDEN: Record<SectionKey, (o: WorkshopOverride | undefined) => boolean> = {
  diagram: (o) => o?.diagram !== undefined,
  crew: (o) => o?.crew !== undefined,
  brief: (o) => o?.brief !== undefined || o?.briefOrder !== undefined,
  intro: (o) => o?.introVideoUrl !== undefined,
  questionnaire: (o) => o?.questionnaire !== undefined,
  "run-sequence": (o) => o?.runSequence !== undefined,
  agenda: (o) => o?.agenda !== undefined,
  bring: (o) => o?.bring !== undefined,
  logistics: (o) => o?.map !== undefined,
  doc: (o) => o?.doc !== undefined,
};

export function WorkshopOverrideEditor({ open, onClose, workshopId }: Props) {
  const isPowerUnlocked = usePowerUser((s) => s.isUnlocked);
  const { user } = useAuth();
  const clientName = useActiveClient((s) => s.clientName);
  const mode = useWorkshopContentMode(workshopId);
  const override = useEffectiveWorkshopOverride(workshopId);
  const [active, setActive] = useState<SectionKey>("diagram");
  const [confirmResetAll, setConfirmResetAll] = useState(false);

  useEffect(() => {
    if (open) {
      void useWorkshopOverrides.getState().hydrate();
      void useWorkshopContentDefaults.getState().hydrate();
    }
  }, [open]);
  useEffect(() => {
    if (!open) setConfirmResetAll(false);
  }, [open]);

  const overriddenKeys = useMemo(
    () => new Set(SECTIONS.filter((s) => SECTION_OVERRIDDEN[s.key](override)).map((s) => s.key)),
    [override],
  );
  const customisedCount = overriddenKeys.size;

  const allowed = isPowerUnlocked && can(user, "manage:program");
  if (!allowed) return null;

  const workshop = WORKSHOPS.find((w) => w.id === workshopId);
  const activeIndex = Math.max(0, SECTIONS.findIndex((s) => s.key === active));
  const ActiveComp = SECTIONS[activeIndex]?.Comp ?? DiagramEditor;
  const activeLabel = SECTIONS[activeIndex]?.label ?? "";
  const pad = (n: number) => String(n).padStart(2, "0");

  const setMode = (next: ContentMode) => {
    if (next === mode) return;
    setContentMode(workshopId, next);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <PanelDialogContent
        className="border-0 bg-transparent p-0 shadow-none"
        style={{ outline: "none", width: "92vw", maxWidth: 920 }}
      >
        <DialogTitle className="sr-only">{content.modalTitle}</DialogTitle>
        <DialogDescription className="sr-only">{content.modalSubtitle}</DialogDescription>
        <div
          className="panel-glow-mc flex flex-col overflow-hidden backdrop-blur"
          style={{ background: "var(--bg-panel-alt)", maxHeight: "min(90vh, 860px)" }}
        >
          {/* Header */}
          <div
            className="flex flex-shrink-0 items-center gap-3 px-4 py-3"
            style={{ borderBottom: "1px solid hsl(var(--border))" }}
          >
            <Settings2 className="h-4 w-4" style={{ color: "var(--accent-true-blue)" }} aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="font-mono text-fs-label font-bold tracking-[0.22em] text-[color:var(--text-muted)] uppercase">
                {content.modalTitle}
              </p>
              <p className="truncate text-fs-label font-medium text-[color:var(--text-primary)]">
                {workshop?.longTitle ?? workshopId}
                {clientName ? ` · ${clientName}` : ""}
              </p>
            </div>
            {customisedCount > 0 ? (
              <span
                className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 font-mono text-fs-micro tracking-[0.18em] uppercase"
                style={{
                  color: "var(--accent-gold)",
                  background: "rgba(var(--accent-gold-rgb), 0.1)",
                  border: "1px solid rgba(var(--accent-gold-rgb), 0.4)",
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: 999,
                    background: "var(--accent-gold)",
                    boxShadow: "0 0 6px rgba(var(--accent-gold-rgb), 0.8)",
                  }}
                />
                {customisedCount} customised
              </span>
            ) : (
              <span
                className="inline-flex flex-shrink-0 items-center rounded-full px-2.5 py-1 font-mono text-fs-micro tracking-[0.18em] text-[color:var(--text-muted)] uppercase"
                style={{ border: "1px solid hsl(var(--border))" }}
              >
                All defaults
              </span>
            )}

            <span
              aria-hidden
              className="h-[18px] w-px flex-shrink-0"
              style={{ background: "hsl(var(--border))" }}
            />

            {confirmResetAll ? (
              <div className="flex items-center gap-1.5">
                <span className="font-mono text-fs-micro tracking-widest text-[color:var(--text-muted)] uppercase">
                  {content.resetAllConfirm}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    clearEffectiveOverrides(workshopId);
                    setConfirmResetAll(false);
                  }}
                  className="rounded-sm border border-[rgba(var(--accent-red-rgb),0.4)] px-1.5 py-0.5 font-mono text-fs-micro tracking-widest text-[color:var(--accent-red)] uppercase"
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmResetAll(false)}
                  className="rounded-sm border border-[rgba(var(--accent-true-blue-rgb),0.2)] px-1.5 py-0.5 font-mono text-fs-micro tracking-widest text-[color:var(--text-secondary)] uppercase"
                >
                  No
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmResetAll(true)}
                disabled={customisedCount === 0}
                className="flex items-center gap-1.5 rounded-md border border-[rgba(var(--accent-true-blue-rgb),0.2)] px-2 py-1 font-mono text-fs-label tracking-widest text-[color:var(--text-secondary)] uppercase transition-colors hover:text-[color:var(--text-primary)] disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:text-[color:var(--text-secondary)]"
              >
                <RotateCcw className="h-3 w-3" aria-hidden />
                {content.resetAll}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex items-center gap-1 rounded-md border px-2 py-1 font-mono font-semibold tracking-widest text-[color:var(--text-secondary)] uppercase"
              style={{ borderColor: "hsl(var(--border))", background: "transparent" }}
            >
              <X aria-hidden style={{ width: 11, height: 11 }} />
              Close
            </button>
          </div>

          {/* Content mode strip */}
          <div
            className="flex flex-shrink-0 flex-wrap items-center gap-3 px-4 py-2.5"
            style={{
              borderBottom: "1px solid hsl(var(--border))",
              background: "rgba(var(--accent-true-blue-rgb), 0.04)",
            }}
          >
            <span className="font-mono text-fs-micro tracking-[0.2em] text-[color:var(--text-muted)] uppercase">
              Content source
            </span>
            <div
              className="inline-flex rounded-md p-0.5"
              style={{ border: "1px solid hsl(var(--border))" }}
              role="group"
              aria-label="Content source"
            >
              <button
                type="button"
                onClick={() => setMode("default")}
                aria-pressed={mode === "default"}
                className="rounded-sm px-2.5 py-1 font-mono text-fs-micro tracking-[0.16em] uppercase transition-colors"
                style={
                  mode === "default"
                    ? {
                        color: "var(--accent-sky)",
                        background: "rgba(var(--accent-true-blue-rgb), 0.2)",
                      }
                    : { color: "var(--text-secondary)" }
                }
              >
                Use default
              </button>
              <button
                type="button"
                onClick={() => setMode("custom")}
                aria-pressed={mode === "custom"}
                className="rounded-sm px-2.5 py-1 font-mono text-fs-micro tracking-[0.16em] uppercase transition-colors"
                style={
                  mode === "custom"
                    ? {
                        color: "var(--accent-gold)",
                        background: "rgba(var(--accent-gold-rgb), 0.14)",
                      }
                    : { color: "var(--text-secondary)" }
                }
              >
                Custom for this client
              </button>
            </div>
            <span className="text-fs-label text-[color:var(--text-muted)]">
              {mode === "default"
                ? "Edits update the shared program template for every client on default."
                : "Edits apply only to this client."}
            </span>
          </div>

          {/* Body: left rail + active section */}
          <div className="flex min-h-0 flex-1">
            <nav
              className="flex w-44 flex-shrink-0 flex-col overflow-y-auto p-2"
              style={{ borderRight: "1px solid hsl(var(--border))" }}
            >
              <p className="px-3 pt-1 pb-2 font-mono text-fs-micro tracking-[0.25em] text-[color:var(--text-muted)] uppercase">
                Sections
              </p>
              <div className="flex flex-col gap-0.5">
                {SECTIONS.map((s) => {
                  const isActive = active === s.key;
                  const isOverridden = overriddenKeys.has(s.key);
                  return (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => setActive(s.key)}
                      aria-current={isActive ? "true" : undefined}
                      className={`flex items-center gap-2 rounded-md px-3 py-2 text-left text-fs-label font-medium outline-none transition-colors duration-150 focus-visible:[box-shadow:0_0_0_2px_rgba(var(--accent-sky-rgb),0.4)] ${
                        isActive
                          ? "bg-[rgba(var(--accent-true-blue-rgb),0.14)] text-[color:var(--accent-sky)] [box-shadow:inset_2px_0_0_0_var(--accent-true-blue)]"
                          : "text-[color:var(--text-secondary)] hover:bg-[rgba(var(--accent-true-blue-rgb),0.06)] hover:text-[color:var(--text-primary)]"
                      }`}
                    >
                      <span className="min-w-0 flex-1 truncate">{s.label}</span>
                      <span
                        aria-hidden
                        title={isOverridden ? "Customised" : undefined}
                        style={{
                          width: 5,
                          height: 5,
                          flexShrink: 0,
                          borderRadius: 999,
                          background: isOverridden ? "var(--accent-gold)" : "transparent",
                          boxShadow: isOverridden
                            ? "0 0 6px rgba(var(--accent-gold-rgb), 0.7)"
                            : "none",
                        }}
                      />
                    </button>
                  );
                })}
              </div>
              <div
                className="mt-auto flex flex-col gap-1.5 px-3 pt-4"
                style={{ borderTop: "1px solid hsl(var(--border))" }}
              >
                <span className="inline-flex items-center gap-1.5 font-mono text-fs-micro tracking-[0.16em] text-[color:var(--text-muted)] uppercase">
                  <span
                    aria-hidden
                    className="h-[5px] w-[5px] rounded-full"
                    style={{ background: "var(--accent-gold)", boxShadow: "0 0 5px rgba(var(--accent-gold-rgb),0.7)" }}
                  />
                  Overridden
                </span>
                <span
                  className="font-mono text-fs-micro tracking-[0.16em] uppercase"
                  style={{ color: customisedCount > 0 ? "var(--accent-sky)" : "var(--text-muted)" }}
                >
                  {customisedCount} / {SECTIONS.length} customised
                </span>
              </div>
            </nav>
            <div className="flex min-h-0 flex-1 flex-col p-4">
              <div className="flex flex-shrink-0 flex-col gap-1 pb-3">
                <span className="font-mono text-fs-label tracking-[0.22em] text-[color:var(--text-muted)] uppercase">
                  Section {pad(activeIndex + 1)} / {pad(SECTIONS.length)}
                </span>
                <h2 className="text-fs-title font-semibold leading-[1.15] tracking-[-0.01em] text-[color:var(--text-primary)]">
                  {activeLabel}
                </h2>
              </div>
              <div className="flex min-h-0 flex-1 flex-col">
                <ActiveComp key={`${workshopId}:${active}:${mode}`} wsId={workshopId} />
              </div>
            </div>
          </div>
        </div>
      </PanelDialogContent>
    </Dialog>
  );
}
